import React, { useCallback, useRef, useState } from 'react';
import { makeStyles } from 'tss-react/mui';
import {
  Button,
  IconButton,
  MenuItem,
  Select,
  Slider,
  Switch,
  Typography,
  Stack,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import type { DesignMap } from '../map';
import { CameraDebugSection } from './CameraDebugSection';
import {
  blendFactorLabels,
  defaultExtrusionRenderParams,
  type BlendFactor,
  type ExtrusionRenderParams,
} from '../threejs/extrusion-render-params.helpers';

type BlendDebugPanelProps = {
  map: DesignMap | undefined;
  onClose: () => void;
};

const useStyles = makeStyles()((theme) => ({
  panel: {
    position: 'absolute',
    top: theme.spacing(2),
    right: theme.spacing(2),
    zIndex: 100,
    width: 320,
    backgroundColor: theme.palette.background.paper,
    borderRadius: theme.shape.borderRadius,
    boxShadow: theme.shadows[4],
    padding: theme.spacing(2),
    pointerEvents: 'auto',
  },
  title: {
    marginBottom: theme.spacing(1.5),
    fontWeight: 600,
  },
  section: {
    marginBottom: theme.spacing(1.5),
  },
  sectionLabel: {
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(0.5),
  },
  row: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: theme.spacing(0.5),
  },
  rowLabel: {
    minWidth: 130,
  },
  slider: {
    flex: 1,
    marginLeft: theme.spacing(1),
  },
  select: {
    flex: 1,
    marginLeft: theme.spacing(1),
    fontSize: 12,
  },
  colorInput: {
    width: 32,
    height: 24,
    padding: 0,
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    cursor: 'pointer',
    backgroundColor: 'transparent',
    marginLeft: theme.spacing(1),
  },
}));

const BLEND_FACTORS = Object.keys(blendFactorLabels) as BlendFactor[];

const rgbToHex = ([r, g, b]: readonly [number, number, number]): string => {
  const h = (v: number) =>
    Math.round(v * 255)
      .toString(16)
      .padStart(2, '0');
  return `#${h(r)}${h(g)}${h(b)}`;
};

const hexToRgb = (hex: string): [number, number, number] => [
  parseInt(hex.slice(1, 3), 16) / 255,
  parseInt(hex.slice(3, 5), 16) / 255,
  parseInt(hex.slice(5, 7), 16) / 255,
];

export const BlendDebugPanel: React.FC<BlendDebugPanelProps> = ({
  map,
  onClose,
}) => {
  const { classes } = useStyles();
  const [params, setParams] = useState<ExtrusionRenderParams>(
    defaultExtrusionRenderParams,
  );

  const snapshot = useRef<ExtrusionRenderParams | null>(null);

  const patch = useCallback(
    (next: Partial<ExtrusionRenderParams>) => {
      setParams((prev) => ({ ...prev, ...next }));
      map?.setExtrusionParams(next);
    },
    [map],
  );

  const copyParams = useCallback(() => {
    snapshot.current = { ...params };
  }, [params]);

  const pasteParams = useCallback(() => {
    if (!snapshot.current) return;
    setParams(snapshot.current);
    map?.setExtrusionParams(snapshot.current);
  }, [map]);

  return (
    <div className={classes.panel}>
      <Stack
        direction="row"
        sx={{
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <Typography className={classes.title} variant="caption">
          Render settings
        </Typography>
        <Stack
          direction="row"
          spacing={0.5}
          sx={{
            alignItems: 'center',
          }}
        >
          <Button size="small" variant="outlined" onClick={copyParams}>
            Copy
          </Button>
          <Button
            size="small"
            variant="outlined"
            disabled={!snapshot.current}
            onClick={pasteParams}
          >
            Paste
          </Button>
          <IconButton
            size="small"
            onClick={onClose}
            aria-label="Close debug panel"
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Stack>
      </Stack>

      <div className={classes.section}>
        <Typography className={classes.sectionLabel} variant="caption">
          Blend mode
        </Typography>
        <div className={classes.row}>
          <Typography className={classes.rowLabel} variant="caption">
            Src
          </Typography>
          <Select
            className={classes.select}
            value={params.blendSrc}
            size="small"
            onChange={(e) => patch({ blendSrc: e.target.value as BlendFactor })}
          >
            {BLEND_FACTORS.map((f) => (
              <MenuItem key={f} value={f}>
                <Typography variant="caption">
                  {blendFactorLabels[f]}
                </Typography>
              </MenuItem>
            ))}
          </Select>
        </div>
        <div className={classes.row}>
          <Typography className={classes.rowLabel} variant="caption">
            Dst
          </Typography>
          <Select
            className={classes.select}
            value={params.blendDst}
            size="small"
            onChange={(e) => patch({ blendDst: e.target.value as BlendFactor })}
          >
            {BLEND_FACTORS.map((f) => (
              <MenuItem key={f} value={f}>
                <Typography variant="caption">
                  {blendFactorLabels[f]}
                </Typography>
              </MenuItem>
            ))}
          </Select>
        </div>
      </div>

      <div className={classes.section}>
        <Typography className={classes.sectionLabel} variant="caption">
          Opacity
        </Typography>
        {(
          [
            ['Floor (storeys)', 'floorOpacity'],
            ['Floor (ground)', 'groundFloorOpacity'],
            ['Roof', 'roofOpacity'],
            ['Wall', 'wallOpacity'],
          ] as const
        ).map(([label, key]) => (
          <div key={key} className={classes.row}>
            <Typography className={classes.rowLabel} variant="caption">
              {label}
            </Typography>
            <Slider
              className={classes.slider}
              value={params[key]}
              min={0}
              max={1}
              step={0.01}
              size="small"
              valueLabelDisplay="auto"
              onChange={(_e, v) => patch({ [key]: v as number })}
            />
          </div>
        ))}
      </div>

      <div className={classes.section}>
        <Typography className={classes.sectionLabel} variant="caption">
          Visibility
        </Typography>
        <Stack
          direction="row"
          sx={{
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Typography variant="caption">Walls</Typography>
          <Switch
            size="small"
            checked={params.showWalls}
            onChange={(e) => patch({ showWalls: e.target.checked })}
          />
        </Stack>
        <Stack
          direction="row"
          sx={{
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Typography variant="caption">Roofs</Typography>
          <Switch
            size="small"
            checked={params.showRoofs}
            onChange={(e) => patch({ showRoofs: e.target.checked })}
          />
        </Stack>
        {(
          [
            ['Depth mask (floors)', 'depthMaskFloors'],
            ['Depth mask (walls)', 'depthMaskWalls'],
            ['Depth mask (roofs)', 'depthMaskRoofs'],
          ] as const
        ).map(([label, key]) => (
          <Stack
            key={key}
            direction="row"
            sx={{
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <Typography variant="caption">{label}</Typography>
            <Switch
              size="small"
              checked={params[key]}
              onChange={(e) => patch({ [key]: e.target.checked })}
            />
          </Stack>
        ))}
      </div>

      <div className={classes.section}>
        <Typography className={classes.sectionLabel} variant="caption">
          Färger
        </Typography>
        {(
          [
            ['Tak', 'roofColor'],
            ['Väggar', 'wallColor'],
            ['Väggar (under mark)', 'undergroundWallColor'],
          ] as const
        ).map(([label, key]) => (
          <div key={key} className={classes.row}>
            <Typography className={classes.rowLabel} variant="caption">
              {label}
            </Typography>
            <input
              type="color"
              className={classes.colorInput}
              value={rgbToHex(params[key])}
              onChange={(e) => patch({ [key]: hexToRgb(e.target.value) })}
            />
          </div>
        ))}
        <Stack
          direction="row"
          sx={{
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Typography variant="caption">Override fill-färg</Typography>
          <Switch
            size="small"
            checked={params.useFloorColorOverride}
            onChange={(e) => patch({ useFloorColorOverride: e.target.checked })}
          />
        </Stack>
        <div className={classes.row}>
          <Typography
            className={classes.rowLabel}
            variant="caption"
            color={
              params.useFloorColorOverride ? 'textPrimary' : 'textSecondary'
            }
          >
            Fill-färg
          </Typography>
          <input
            type="color"
            className={classes.colorInput}
            disabled={!params.useFloorColorOverride}
            value={rgbToHex(params.floorColor)}
            onChange={(e) => patch({ floorColor: hexToRgb(e.target.value) })}
          />
        </div>
      </div>

      <div className={classes.section}>
        <Stack
          direction="row"
          sx={{
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Typography className={classes.sectionLabel} variant="caption">
            Ljus
          </Typography>
          <Switch
            size="small"
            checked={params.useLightOverride}
            onChange={(e) => patch({ useLightOverride: e.target.checked })}
          />
        </Stack>
        {(
          [
            ['Ambient', 'lightAmbient', 0, 1, 0.01],
            ['Strength', 'lightStrength', 0, 1, 0.01],
            ['Dir X', 'lightDirX', -1, 1, 0.01],
            ['Dir Y', 'lightDirY', -1, 1, 0.01],
            ['Dir Z', 'lightDirZ', -1, 1, 0.01],
          ] as const
        ).map(([label, key, min, max, step]) => (
          <div key={key} className={classes.row}>
            <Typography
              className={classes.rowLabel}
              variant="caption"
              color={params.useLightOverride ? 'textPrimary' : 'textSecondary'}
            >
              {label}
            </Typography>
            <Slider
              className={classes.slider}
              value={params[key]}
              min={min}
              max={max}
              step={step}
              size="small"
              disabled={!params.useLightOverride}
              valueLabelDisplay="auto"
              onChange={(_e, v) => patch({ [key]: v as number })}
            />
          </div>
        ))}
      </div>

      <div className={classes.section}>
        <CameraDebugSection map={map} />
      </div>
    </div>
  );
};
